/**
 * AnalystNote — Single-page interleaved layout for the Executive Brief.
 * 
 * Mirrors sell-side research note format: narrative sections with supporting
 * visualizations inline, reading top-to-bottom as one cohesive deliverable.
 * 
 * Layout:
 *   Header (company, timestamp, sources)
 *   Executive Summary + key chart
 *   SWO Analysis
 *   Key Financials + financial chart
 *   Risk Overview + risk heatmap
 *   Citations
 *   Disclaimer footer
 */

import React from 'react';
import { SpaceBetween, Box, Header } from '@cloudscape-design/components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import Plot from 'react-plotly.js';
import MarketContextChart from './MarketContextChart';
import { useTheme } from '../../context/ThemeContext';
import { getTableStyles } from '../../styles/tableStyles';
import { isNumericContent } from '../../utils/alignmentHeuristic';

function AnalystNote({ reportData, isGenerating = false, onCitationClick }) {
  const { isDark, isSummit } = useTheme();

  // Theme colors
  const c = isDark ? {
    bg: '#0a1628',
    cardBg: '#0f1f35',
    border: '#1e3a5f',
    heading: '#e2e8f0',
    body: '#cbd5e1',
    secondary: '#94a3b8',
    accent: '#60a5fa',
    divider: '#1e3a5f',
    disclaimerBg: '#1e293b',
    disclaimerText: '#94a3b8',
  } : {
    bg: '#ffffff',
    cardBg: '#f8f9fa',
    border: '#e9ecef',
    heading: '#232f3e',
    body: '#16191f',
    secondary: '#495057',
    accent: '#0073bb',
    divider: '#e9ecef',
    disclaimerBg: '#f8f9fa',
    disclaimerText: '#6c757d',
  };

  const ts = getTableStyles(isDark, isSummit);

  const sections = reportData?.narrative?.sections || {};
  const metadata = reportData?.metadata || {};
  const citations = reportData?.citations || [];
  const visualizations = reportData?.visualizations || {};
  const charts = visualizations.charts || [];
  const kpis = visualizations.kpis || [];

  // Find specific chart types from the visualization data
  const findChart = (keywords) => {
    return charts.find(chart => {
      const title = (chart.title || '').toLowerCase();
      return keywords.some(kw => title.includes(kw));
    });
  };

  // Find charts — only use ones that have actual data
  const revenueChart = findChart(['revenue', 'financial', 'segment', 'growth']);
  const riskChart = (() => {
    const chart = findChart(['risk', 'heatmap', 'severity']);
    // Hide risk chart if it has no meaningful data
    if (chart && chart.data) {
      const data = Array.isArray(chart.data) ? chart.data : [chart.data];
      const hasData = data.some(d => d.z && d.z.length > 0 && d.z.some(row => row.some(v => v > 0)));
      if (!hasData) return null;
    }
    return chart;
  })();
  // Hide confidence chart — not needed for the demo
  const confidenceChart = null;

  // Skeleton placeholder for sections still loading
  const renderSkeleton = () => (
    <div style={{ opacity: 0.5, padding: '16px 0' }}>
      {[100, 85, 92, 70, 88, 95, 60].map((width, i) => (
        <div key={i} style={{
          height: '12px',
          width: `${width}%`,
          background: isDark
            ? 'linear-gradient(90deg, #1a2332 25%, #243447 50%, #1a2332 75%)'
            : 'linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%)',
          borderRadius: '4px',
          marginBottom: '10px',
          backgroundSize: '200% 100%',
          animation: 'shimmer 1.5s infinite',
        }} />
      ))}
      <style>{`
        @keyframes shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );

  // Markdown renderer
  let rowIndex = 0;
  const renderMarkdown = (text) => {
    if (!text) return null;
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          h1: ({ children }) => <h1 style={{ margin: '20px 0 10px', fontWeight: 600, fontSize: '1.2em', color: c.heading }}>{children}</h1>,
          h2: ({ children }) => <h2 style={{ margin: '18px 0 8px', fontWeight: 600, fontSize: '1.1em', color: c.heading }}>{children}</h2>,
          h3: ({ children }) => <h3 style={{ margin: '14px 0 6px', fontWeight: 600, fontSize: '1.05em', color: c.heading }}>{children}</h3>,
          p: ({ children }) => <p style={{ marginBottom: '12px', lineHeight: '1.6', color: c.body }}>{children}</p>,
          ul: ({ children }) => <ul style={{ marginBottom: '14px', paddingLeft: '18px', lineHeight: '1.6' }}>{children}</ul>,
          ol: ({ children }) => <ol style={{ marginBottom: '14px', paddingLeft: '18px', lineHeight: '1.6' }}>{children}</ol>,
          li: ({ children }) => <li style={{ marginBottom: '6px', color: c.body }}>{children}</li>,
          strong: ({ children }) => {
            const text = typeof children === 'string' ? children : Array.isArray(children) ? children.join('') : String(children);
            if (text === 'HIGH' || text === 'CRITICAL') return <strong style={{ color: '#d62728', fontWeight: 600 }}>{children}</strong>;
            if (text === 'MEDIUM') return <strong style={{ color: '#e67e22', fontWeight: 600 }}>{children}</strong>;
            if (text === 'LOW') return <strong style={{ color: '#16a085', fontWeight: 600 }}>{children}</strong>;
            if (/^\$?\d/.test(text) || /\d+%/.test(text)) return <strong style={{ color: c.accent, fontWeight: 700 }}>{children}</strong>;
            return <strong style={{ fontWeight: 600, color: c.heading }}>{children}</strong>;
          },
          table: ({ children }) => <div style={ts.wrapper}><table style={ts.table}>{children}</table></div>,
          thead: ({ children }) => <thead style={ts.thead}>{children}</thead>,
          th: ({ children }) => <th style={ts.th}>{children}</th>,
          tbody: ({ children }) => { rowIndex = 0; return <tbody>{children}</tbody>; },
          tr: ({ children, isHeader }) => { if (isHeader) return <tr>{children}</tr>; const idx = rowIndex++; return <tr style={idx % 2 === 1 ? ts.trAlt : {}}>{children}</tr>; },
          td: ({ children }) => { const text = typeof children === 'string' ? children : ''; return <td style={isNumericContent(text) ? ts.tdNumeric : ts.td}>{children}</td>; },
        }}
      >
        {text}
      </ReactMarkdown>
    );
  };

  // Render an inline chart
  const renderInlineChart = (chart) => {
    if (!chart || !chart.data) return null;
    const data = Array.isArray(chart.data) ? chart.data : [chart.data];
    const layout = {
      autosize: true,
      margin: { l: 50, r: 30, t: 40, b: 50 },
      font: { family: 'Amazon Ember, sans-serif', size: 11, color: isDark ? '#cbd5e1' : '#16191f' },
      paper_bgcolor: 'transparent',
      plot_bgcolor: isDark ? '#0f1f35' : '#f8f9fa',
      showlegend: true,
      legend: { orientation: 'h', y: -0.2, x: 0.5, xanchor: 'center' },
      ...(chart.layout || {}),
      title: { text: chart.title, font: { size: 13, color: c.heading } },
    };
    return (
      <div style={{
        margin: '16px 0',
        padding: '16px',
        borderRadius: 8,
        border: `1px solid ${c.border}`,
        background: c.cardBg,
      }}>
        <Plot
          data={data}
          layout={layout}
          config={{ displayModeBar: false, responsive: true }}
          style={{ width: '100%', height: '280px' }}
          useResizeHandler={true}
        />
      </div>
    );
  };

  // Render a section with optional inline chart
  const renderSection = (sectionKey, icon, title, borderColor, chart) => {
    const content = sections[sectionKey];
    const hasContent = content && content.length > 0;

    return (
      <div style={{
        borderLeft: `4px solid ${borderColor}`,
        paddingLeft: '20px',
        marginBottom: '32px',
        animation: hasContent ? 'fadeIn 0.5s ease' : 'none',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <span style={{ fontSize: '1.2em' }}>{icon}</span>
          <span style={{ fontSize: '1.1em', fontWeight: 600, color: c.heading }}>{title}</span>
          {isGenerating && !hasContent && (
            <span style={{
              fontSize: 11,
              color: '#0972d3',
              background: 'rgba(9, 114, 211, 0.1)',
              border: '1px solid rgba(9, 114, 211, 0.3)',
              borderRadius: 4,
              padding: '2px 8px',
              animation: 'pulse 1.5s infinite',
            }}>generating...</span>
          )}
        </div>

        {hasContent ? (
          <>
            <div style={{ lineHeight: '1.6' }}>{renderMarkdown(content)}</div>
            {chart && renderInlineChart(chart)}
          </>
        ) : (
          isGenerating && renderSkeleton()
        )}
      </div>
    );
  };

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      {/* Report Header */}
      <div style={{
        borderBottom: `2px solid ${c.divider}`,
        paddingBottom: '16px',
        marginBottom: '28px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.4em', fontWeight: 700, color: c.heading }}>
              📊 RESEARCH BRIEFING
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '0.85em', color: c.secondary }}>
              Generated: {reportData?.generated_at ? new Date(reportData.generated_at).toLocaleString() : 'In progress...'}
              {metadata.documents_analyzed && metadata.documents_analyzed.length > 0 && (
                <> | Sources: {metadata.documents_analyzed.slice(0, 3).join(', ')}</>
              )}
              {metadata.generation_time_seconds && (
                <> | Generated in {Math.round(metadata.generation_time_seconds)}s</>
              )}
            </p>
          </div>
        </div>
      </div>

      {/* KPI Strip */}
      {kpis.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${Math.min(kpis.length, 4)}, 1fr)`,
          gap: '12px',
          marginBottom: '28px',
          padding: '16px',
          borderRadius: 8,
          background: c.cardBg,
          border: `1px solid ${c.border}`,
        }}>
          {kpis.slice(0, 4).map((kpi, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.75em', color: c.secondary, marginBottom: '4px' }}>{kpi.label}</div>
              <div style={{ fontSize: '1.3em', fontWeight: 700, color: c.heading }}>{kpi.value}</div>
              {kpi.trend && (
                <div style={{ fontSize: '0.8em', color: kpi.trend_direction === 'up' ? '#2ca02c' : kpi.trend_direction === 'down' ? '#d62728' : c.secondary }}>
                  {kpi.trend_direction === 'up' ? '↑' : kpi.trend_direction === 'down' ? '↓' : '→'} {kpi.trend}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Sections — interleaved with charts */}
      {renderSection('executive_summary', '📊', 'Executive Summary', '#0073bb', revenueChart)}

      {/* Market Context — share price inline after summary */}
      {!isGenerating && sections.executive_summary && (
        <div style={{
          margin: '0 0 32px 0',
          padding: '16px',
          borderRadius: 8,
          border: `1px solid ${c.border}`,
          background: c.cardBg,
        }}>
          <MarketContextChart />
        </div>
      )}

      {renderSection('swo_analysis', '💪', 'Strengths, Weaknesses & Opportunities', '#16a085', null)}
      {renderSection('key_financials', '💰', 'Key Financials', '#2c6fba', null)}
      {renderSection('risk_overview', '⚠️', 'Risk Overview', '#e67e22', riskChart)}

      {/* Citations */}
      {!isGenerating && citations.length > 0 && (
        <div style={{
          borderTop: `1px solid ${c.divider}`,
          paddingTop: '20px',
          marginTop: '28px',
        }}>
          <h3 style={{ fontSize: '0.95em', fontWeight: 600, color: c.heading, marginBottom: '12px' }}>
            CITATIONS
          </h3>
          <div style={{ fontSize: '0.85em', color: c.secondary, lineHeight: '1.8' }}>
            {citations
              .filter(cit => cit.document || cit.document_name || cit.text)
              .slice(0, 10)
              .map((cit, i) => {
                const doc = cit.document || cit.document_name || 'Source';
                const page = cit.page || cit.page_number;
                return (
                  <div key={i} style={{ marginBottom: '4px' }}>
                    <span style={{ color: c.accent, fontWeight: 600 }}>[{i + 1}]</span>{' '}
                    {doc}{page && page !== 'N/A' ? `, p.${page}` : ''}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Disclaimer Footer */}
      <div style={{
        marginTop: '32px',
        padding: '12px 16px',
        borderRadius: 6,
        background: c.disclaimerBg,
        border: `1px solid ${c.border}`,
        textAlign: 'center',
      }}>
        <span style={{ fontSize: '0.8em', color: c.disclaimerText }}>
          ⚠️ AI-generated research summary. Not financial advice. For informational purposes only.
        </span>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default AnalystNote;
